import { useMemo, useState } from 'react';
import * as Plot from '@observablehq/plot';
import PlotFigure from '../../PlotFigure';
import ChartOverlay from '../common/ChartOverlay';

const sectionStyle = {
    marginTop: '24px',
    display: 'grid',
    gap: '24px',
    gridTemplateColumns: 'minmax(0, 1fr)',
};

const normaliseEvents = (items) => {
    if (!Array.isArray(items)) {
        return [];
    }

    return items
        .map((item) => {
            const timestamp = item?.ts || item?.timestamp || item?.ingested_at || item?.ingestedAt;
            if (!timestamp) {
                return null;
            }

            const when = new Date(timestamp);
            if (Number.isNaN(when.getTime())) {
                return null;
            }

            const code = item?.code !== undefined && item?.code !== null ? String(item.code) : 'unknown';
            const error = item?.error || item?.message || 'Alert triggered';

            return {
                code,
                when,
                error,
                test_type: item?.test_type,
                metadata: item?.metadata,
            };
        })
        .filter(Boolean)
        .sort((a, b) => a.when.getTime() - b.when.getTime());
};

const groupByHour = (events) => {
    const buckets = new Map();

    events.forEach((event) => {
        const bucketTime = new Date(event.when.getTime());
        bucketTime.setMinutes(0, 0, 0);

        const key = `${event.code}-${bucketTime.toISOString()}`;
        const existing = buckets.get(key) || {
            code: event.code,
            time: bucketTime,
            count: 0,
        };

        existing.count += 1;
        buckets.set(key, existing);
    });

    return Array.from(buckets.values()).sort((a, b) => a.time.getTime() - b.time.getTime());
};

function AlertsChart({ items }) {
    const events = useMemo(() => normaliseEvents(items), [items]);
    const aggregated = useMemo(() => groupByHour(events), [events]);
    const errorTimeline = useMemo(
        () =>
            events.map((event) => ({
                time: event.when,
                error: event.error || 'Unspecified',
                code: event.code,
            })),
        [events]
    );
    const errorDomain = useMemo(
        () => Array.from(new Set(events.map((event) => event.error || 'Unspecified'))),
        [events]
    );
    const [expanded, setExpanded] = useState(false);
    const [expandedErrorPlot, setExpandedErrorPlot] = useState(false);
    const description = 'Aggregated alert counts per hour grouped by alert code. Hover for exact counts and timestamps.';

    const plotOptions = useMemo(
        () => ({
            x: { type: 'time', grid: true, label: 'Timestamp' },
            y: { grid: true, label: 'Alerts per hour' },
            color: {
                legend: true,
                type: 'ordinal',
                range: [
                    '#4df7ff',
                    '#ff6bd6',
                    '#cfa4ff',
                    '#ffb74d',
                    '#8df0a1',
                    '#ffd93d',
                ],
            },
            marks: [
                Plot.ruleY([0]),
                Plot.line(aggregated, {
                    x: 'time',
                    y: 'count',
                    stroke: 'code',
                }),
                Plot.dot(aggregated, {
                    x: 'time',
                    y: 'count',
                    fill: 'code',
                    title: (d) => `Code ${d.code}\n${d.count} alerts\n${d.time.toLocaleString()}`,
                }),
                Plot.tip(
                    aggregated,
                    Plot.pointerX({
                        x: 'time',
                        y: 'count',
                        stroke: 'code',
                        title: (d) => `Code ${d.code}\n${d.count} alerts\n${d.time.toLocaleString()}`,
                        pointer: 'mouse',
                        className: 'plot-tip neon-plot-tip',
                        textPadding: 12,
                        pointerSize: 14,
                        lineHeight: 1.6,
                        fontSize: 12,
                        fontWeight: '600',
                    })
                ),
            ],
        }),
        [aggregated]
    );

    if (!aggregated.length) {
        return <p>No alerts recorded for the selected interval.</p>;
    }

    const recent = events.slice(-5).reverse();

    return (
        <div style={sectionStyle}>
            <section className="plot-section alerts-chart">
                <header className="plot-section__header">
                    <div className="plot-section__header-text">
                        <h3>Alert volume by code</h3>
                        <p className="plot-description">{description}</p>
                    </div>
                    <button type="button" className="plot-expand-button" onClick={() => setExpanded(true)}>
                        ⤢ Expand
                    </button>
                </header>
                <PlotFigure options={plotOptions} />
            </section>

            <section className="plot-section alerts-error-plot">
                <header className="plot-section__header">
                    <div className="plot-section__header-text">
                        <h3>Error messages over time</h3>
                        <p className="plot-description">Track when individual error messages occurred for the selected device.</p>
                    </div>
                    <button type="button" className="plot-expand-button" onClick={() => setExpandedErrorPlot(true)}>
                        ⤢ Expand
                    </button>
                </header>
                <PlotFigure
                    options={{
                        x: { type: 'time', grid: true },
                        y: { type: 'band', tickFormat: () => '' },
                        color: {
                            legend: true,
                            type: 'ordinal',
                            domain: errorDomain,
                            range: [
                                '#4df7ff',
                                '#ff6bd6',
                                '#ffd93d',
                                '#8df0a1',
                                '#cfa4ff',
                                '#ff9f68',
                            ].slice(0, errorDomain.length || 1),
                        },
                        marks: [
                            Plot.dot(errorTimeline, {
                                x: 'time',
                                y: 'error',
                                fill: 'error',
                                r: 6.5,
                                title: (d) => `${d.error}\n${d.time.toLocaleString()}`,
                            }),
                        ],
                    }}
                    height={320}
                />
            </section>

            {recent.length ? (
                <aside className="alerts-summary">
                    <h4>Most recent alerts</h4>
                    <ul>
                        {recent.map((event, index) => (
                            <li key={`${event.code}-${event.when.toISOString()}-${index}`}>
                                <div className="alerts-summary__code">Code {event.code}</div>
                                <div className="alerts-summary__message">{event.error}</div>
                                {event.test_type && <div className="alerts-summary__extras">Type: {event.test_type}</div>}
                                {event.metadata && <div className="alerts-summary__extras" style={{ fontSize: '0.85em', opacity: 0.8 }}>{JSON.stringify(event.metadata)}</div>}
                                <div className="alerts-summary__timestamp">{event.when.toLocaleString()}</div>
                            </li>
                        ))}
                    </ul>
                </aside>
            ) : null}

            {expanded ? (
                <ChartOverlay
                    title="Alert volume by code"
                    description={description}
                    onClose={() => setExpanded(false)}
                >
                    <PlotFigure options={plotOptions} height="min(72vh, 680px)" className="chart-overlay__plot" />
                </ChartOverlay>
            ) : null}

            {expandedErrorPlot ? (
                <ChartOverlay
                    title="Error messages over time"
                    description="Track when each error message occurred for this device."
                    onClose={() => setExpandedErrorPlot(false)}
                >
                    <PlotFigure
                        options={{
                            x: { type: 'time', grid: true },
                            y: { type: 'band', tickFormat: () => '' },
                            color: {
                                legend: true,
                                type: 'ordinal',
                                domain: errorDomain,
                                range: [
                                    '#4df7ff',
                                    '#ff6bd6',
                                    '#ffd93d',
                                    '#8df0a1',
                                    '#cfa4ff',
                                    '#ff9f68',
                                ].slice(0, errorDomain.length || 1),
                            },
                            marks: [
                                Plot.dot(errorTimeline, {
                                    x: 'time',
                                    y: 'error',
                                    fill: 'error',
                                    r: 7,
                                    title: (d) => `${d.error}\n${d.time.toLocaleString()}`,
                                }),
                            ],
                        }}
                        height="min(72vh, 640px)"
                        className="chart-overlay__plot"
                    />
                </ChartOverlay>
            ) : null}
        </div>
    );
}

export default AlertsChart;
